import { useEffect, useRef, useState } from 'react';
import {
  ArrowRightOutlined,
  InstagramOutlined,
  LinkedinOutlined,
  MailOutlined,
  XOutlined,
  YoutubeOutlined,
} from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '@/components/Logo';
import { useLang } from '@/hooks/useLang';
import { pick } from '@/lib/localized';
import { site } from '@/lib/config';
import { CATEGORIES } from '@/data/programs';

type FooterLink = { to: string; label: string };

// Ana sayfanın ORTAK koyu-lacivert ailesi (#030616) — Paydaşlar ve tüm koyu
// bölümlerle aynı zemin dili.
const FOOTER_BG = 'linear-gradient(180deg, #060a1c 0%, #030616 100%)';

const cl = (v: number) => Math.min(1, Math.max(0, v));

// Başlık harf harf "yazılarak" tamamlanır (on=true olunca sıralı belirir).
const TypedTitle = ({ text, on }: { text: string; on: boolean }) => (
  <>
    {Array.from(text).map((ch, i) => (
      <span
        key={i}
        style={{
          opacity: on ? 1 : 0,
          transition: 'opacity 0.3s ease',
          transitionDelay: on ? `${i * 26}ms` : '0ms',
        }}
      >
        {ch}
      </span>
    ))}
  </>
);

// Footer ayırıcı çizgisi — sağdan yüksek gelip ortada "K" kancasıyla kırılır,
// solda alçalarak kenara uzanır (tasarım SVG'si, viewBox 2071×322).
const LINE_PATH =
  'M2070.36 0.5H1128.32C1128.32 0.5 1068.92 5.59464 1031.89 60.8901C994.864 116.185 859.422 320.344 859.422 320.344L787.351 210.374H0';

// Kapanma panelleri — kenarları LINE_PATH konturunun KENDİSİ olan iki kapalı
// bölge: üst panel çizginin üstünde kalan alan (yukarı sonsuza uzar), alt panel
// altında kalan alan (aşağı sonsuza uzar). İkisi kapanınca çizgide birleşir.
const JAW_TOP = `${LINE_PATH}V-6000H2071V0.5Z`;
const JAW_BOTTOM = `${LINE_PATH}V6400H2071V0.5Z`;

const LinkColumn = ({
  title,
  links,
}: {
  title: string;
  links: FooterLink[];
}) => (
  <div>
    <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--footer-accent)]">
      {title}
    </h3>
    <ul className="mt-4 space-y-2.5">
      {links.map((l) => (
        <li key={l.to}>
          <Link
            to={l.to}
            className="group inline-flex items-center gap-1.5 text-sm text-[#CED1D1]/75 transition-colors hover:text-white"
          >
            <span className="h-px w-0 bg-[var(--footer-accent)] transition-all duration-300 group-hover:w-3" />
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

// embedded: EventsPortal'ın pinli sahnesinin İÇİNDE, Paydaşlar'ın tam üzerinde
// aynı konumda opaklıkla (reveal) belirir → kaymaz, "içinden gelir".
// standalone: normal footer (mobil / diğer sayfalar).
const Footer = ({
  embedded = false,
  reveal = 1,
}: {
  embedded?: boolean;
  reveal?: number;
}) => {
  const { t } = useTranslation();
  const lang = useLang();
  const year = new Date().getFullYear();
  // Footer'ın kendi arka planı YOKTUR: sayfanın sabit zemini (degrade +
  // desen) footer'ın arkasında kesintisiz devam eder. Eskiden sayfa sayfa
  // şeffaflık listesi tutuluyordu; artık her sayfada şeffaf.
  const { pathname } = useLocation();
  // Footer accent rengi sayfaya göre: terminal turuncu, eklem yeşil, next-level
  // turuncu; diğer sayfalarda (ana sayfa dâhil) varsayılan mavi.
  const [accent, accentHover] =
    (
      {
        '/program/terminal-call': ['#fe6a00', '#ff7d1a'],
        '/community': ['#46c68c', '#5bd6a0'],
        // Teknik Gezi başvurusu — form sayfasının teal vurgusu
        '/technical-visit': ['#2dd4bf', '#5eead4'],
        '/program/next-level': ['#fd6a00', '#ff7d1a'],
        '/program/engineerhub': ['#ff8500', '#ff9c33'],
        '/program/bistlab': ['#0093ac', '#00b3d1'],
        '/program/water-hackathon': ['#22b8d4', '#3ecbe4'],
        '/program/rota-tech-development': ['#6f76d8', '#8f95e6'],
        '/program/tasting-workshops': ['#f5a524', '#ffb84d'],
        '/program/prototype-acceleration': ['#ffb600', '#ffd166'],
        '/program/defense-industry-development': ['#4e9bda', '#6fb0e6'],
        '/program/agriculture-ideathon': ['#D79328', '#e5a93f'],
        '/program/agriculture-technologies': ['#D79328', '#e5a93f'],
        '/program/academy': ['#f2b53c', '#f7c862'],
        '/program/smart-city': ['#38bdf8', '#67cffb'],
        '/program/project-capacity-development': ['#8b7cf8', '#a89bfb'],
        '/program/entrepreneurship': ['#46c68c', '#5bd6a0'],
        '/program/agriculture-workshop': ['#8DC63F', '#a4d95e'],
        // Emlak Konut Ideathonu — logodan gelen kurumsal mavi
        '/program/real-estate-ideathon': ['#005DAD', '#2f8ad4'],
        // Tamamlanan Projeler — sayfanın tek zeminiyle uyumlu azure
        '/programs/projects': ['#7ea6ff', '#a4c3ff'],
        // Ekosistem Geliştirme format sayfaları — sayfa temalarıyla aynı accent
        '/program/hackathon': ['#3d84ff', '#67a1ff'],
        '/program/ideathon': ['#f5a524', '#ffb84d'],
        '/program/workshop': ['#46c68c', '#5bd6a0'],
        '/radar': ['#BE1E2D', '#d94654'],
      } as Record<string, [string, string]>
    )[pathname] ??
    (pathname.startsWith('/events')
      ? ['#3d84ff', '#67a1ff']
      : pathname.startsWith('/press')
        ? ['#7ea6ff', '#a4c3ff']
        : ['#4f7dff', '#7ea6ff']); // varsayılan: frame altı neon SVG mavisi
  const accentVars = {
    '--footer-accent': accent,
    '--footer-accent-hover': accentHover,
  } as React.CSSProperties;
  // Çizgi rengi: her sayfada footer'ın kendi vurgu rengi (varsayılanda azure,
  // program sayfalarında sayfanın accent'i) → paletle her zaman uyumlu.
  const lineAccent = accent;

  // Standalone modda: görünüme girince kademeli beliriş (yalnızca opaklık).
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(embedded);

  // ANA SAYFA: footer normal akışta ama ESKİ kapanma efekti (üst/alt çizgi
  // şekilli paneller) kendi scroll'uyla sürülür — footer görünür alana
  // girdikçe reveal 0→1 ilerler, paneller kapanır, neon çizgi tamamlanır.
  // YALNIZ lg+: pinli tam ekran sahne mobilde içeriği kırpıyordu (alt bar ve
  // sütunlar görünmüyordu) → mobilde normal akış (standalone) kullanılır.
  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const homeSelf = !embedded && pathname === '/' && isDesktop;
  const [selfReveal, setSelfReveal] = useState(0);
  useEffect(() => {
    if (!homeSelf) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        // Footer, paydaş parkurunun (220vh) TAMAMINA bindirilmiştir ve içi
        // sticky'dir → footer AŞAĞIDAN KAYMAZ, paydaşların üzerinde YERİNDE
        // belirir. Parkur ilerlemesinin %45'inden sonra paneller kapanmaya
        // başlar, %95'te tamamlanır.
        const denom = Math.max(1, r.height - vh);
        const raw = cl(-r.top / denom);
        // %60'a kadar yalnız paydaşlar izlenir (bekleme); kapanma 0.60→0.98
        // aralığında gerçekleşir.
        const nextRev = cl((raw - 0.6) / 0.38);
        setSelfReveal(nextRev);
        // Paydaşlar, paneller DAHA GELMEDEN tamamen söner: %42–%56 aralığında
        // 0→1 (paneller %60'ta başlar → kapanırken arkada kimse yok).
        document.documentElement.style.setProperty(
          '--home-stake-fade',
          String(cl((raw - 0.42) / 0.14)),
        );
        // Jaw konturu her karede yeniden ölçülür — font/görsel yüklenmesiyle
        // oluşan yerleşim kaymalarında çizgiyle hizası bozulmasın (sapma fix).
        const root = rootRef.current;
        const line = lineSvgRef.current;
        if (root && line) {
          const fr = root.getBoundingClientRect();
          const lr = line.getBoundingClientRect();
          if (fr.width && fr.height && lr.width) {
            const next = {
              x0: (lr.left - fr.left) / fr.width,
              y0: (lr.top - fr.top) / fr.height,
              sx: lr.width / fr.width / 2071,
              sy: lr.height / fr.height / 322,
            };
            setJaw((prev) =>
              Math.abs(prev.y0 - next.y0) > 0.0005 ||
              Math.abs(prev.sy - next.sy) > 0.000005 ||
              Math.abs(prev.x0 - next.x0) > 0.0005
                ? next
                : prev,
            );
          }
        }
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      document.documentElement.style.removeProperty('--home-stake-fade');
    };
  }, [homeSelf]);

  // Kapanma panelli mod: gömülü (EventsPortal) VEYA ana sayfa self-reveal.
  const jawsMode = embedded || homeSelf;
  const rev = embedded ? reveal : selfReveal;

  // Gömülü kapanma panellerinin, RENDER EDİLEN çizgi SVG'sinin gerçek konum ve
  // ölçüsüne birebir kilitlenmesi: footer kökü + çizgi svg'si ölçülür,
  // objectBoundingBox dönüşümü (x0,y0,sx,sy) hesaplanır → kontur tam çizginin
  // kullanıldığı yere denk gelir.
  const rootRef = useRef<HTMLDivElement>(null);
  const lineSvgRef = useRef<SVGSVGElement>(null);
  const [jaw, setJaw] = useState({
    x0: 0,
    y0: 0.16,
    sx: 1 / 2071,
    sy: 0.15 / 322,
  });
  useEffect(() => {
    if (!jawsMode) return;
    const measure = () => {
      const root = rootRef.current;
      const line = lineSvgRef.current;
      if (!root || !line) return;
      const fr = root.getBoundingClientRect();
      const lr = line.getBoundingClientRect();
      if (!fr.width || !fr.height || !lr.width) return;
      setJaw({
        x0: (lr.left - fr.left) / fr.width,
        y0: (lr.top - fr.top) / fr.height,
        sx: lr.width / fr.width / 2071,
        sy: lr.height / fr.height / 322,
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [jawsMode]);
  useEffect(() => {
    if (embedded) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [embedded]);

  // Sütunlar navbar'daki menü yapısını birebir izler:
  // Programlar → Ekosistem Geliştirme → Keşfet → Kurumsal.
  const programs: FooterLink[] = [
    ...CATEGORIES.filter(
      (c) =>
        // 'focus' aşağıda elle eklenir → listede iki kez çıkmasın.
        !['events', 'mentorship', 'collaboration', 'focus'].includes(c.slug),
    ).map((c) => ({
      to: `/programs/${c.slug}`,
      label: pick(c.title, lang),
    })),
    {
      to: '/programs/focus',
      label: lang === 'en' ? 'Focus Areas' : 'Odak Alanları',
    },
  ];
  const ecosystem: FooterLink[] = [
    { to: '/program/hackathon', label: 'Hackathon' },
    { to: '/program/ideathon', label: 'Ideathon' },
    {
      to: '/program/workshop',
      label: lang === 'en' ? 'Workshop' : 'Çalıştay',
    },
  ];
  const discover: FooterLink[] = [
    { to: '/events', label: t('nav.events') },
    { to: '/press', label: t('nav.press') },
    // Navbar'daki Keşfet menüsüyle aynı sıra
    { to: '/radar', label: 'Kapsül Radar' },
  ];
  const corporate: FooterLink[] = [
    { to: '/institutional', label: t('nav.about') },
    // Teknik Gezi — Kurumsal sayfasının "Geçmişten Geleceğe" bölümüne iner
    // (yerleşke tanıtımı + ziyaret başvurusu butonu orada).
    {
      to: '/institutional#gecmisten-gelecege',
      label: lang === 'en' ? 'Technical Visit' : 'Teknik Gezi',
    },
    { to: '/contact', label: t('nav.contact') },
  ];

  const socials = [
    {
      href: site.social.instagram,
      icon: <InstagramOutlined />,
      label: 'Instagram',
    },
    { href: site.social.x, icon: <XOutlined />, label: 'X' },
    {
      href: site.social.linkedin,
      icon: <LinkedinOutlined />,
      label: 'LinkedIn',
    },
    { href: site.social.youtube, icon: <YoutubeOutlined />, label: 'YouTube' },
  ];

  // Kademeli giriş — yalnızca opaklık (kayma yok).
  const rise = `transition-opacity duration-700 ease-out ${
    shown ? 'opacity-100' : 'opacity-0'
  }`;
  const delay = (ms: number) => ({
    transitionDelay: shown ? `${ms}ms` : '0ms',
  });

  // Neon çizim KAPANIŞLA SENKRON: paneller kapanırken çizgi soldan sağa
  // çizilir ve paneller birleştiği anda (reveal 0.6) çizgi TAMAMLANMIŞ olarak
  // birleşme konturunun üzerinde parlar. Standalone'da CSS geçişiyle çizilir.
  const drawT = jawsMode ? cl((rev - 0.05) / 0.55) : shown ? 1 : 0;
  const textsOn = jawsMode ? rev > 0.55 : shown;
  // Path SAĞDAN (x=2070) başlayıp SOLDA (x=0) bittiği için pozitif dashoffset
  // sağdan-sola çizerdi; NEGATİF offset path'in SONUNDAN başlatır → çizim
  // SOLDAN SAĞA akar.
  const dashOffset = drawT - 1;

  // Ortak iç içerik (marka ifadesi + link sütunları + alt bar).
  const body = (
    <>
      {/* İçerik dikeyde ortalanır → footer tam ekranı doldurur. */}
      <div className="relative flex flex-1 flex-col justify-center">
        {/* ── Üst bant: tam genişlik footer çizgisi + marka ifadesi ──
            Çizgi full-bleed SVG ve DOĞAL oranında (preserveAspectRatio) çizilir
            → hiçbir ekranda şekli bozulmaz. Dar ekranlarda min genişlik verilir;
            taşan sağ kısım kırpılır (soldaki alçak segment + kanca görünür kalır).
            Başlık: lg ve üzeri ekranlarda çizginin üzerine bindirilir (mock),
            daha dar ekranlarda çakışmaması için çizginin üstünde normal akışta. */}
        {/* Mobil CTA — BAŞLIK çizginin ÜSTÜNDE; alt yazı ve "Bize Ulaşın"
            butonu çizginin ALTINDA (aşağıdaki blok). Masaüstünde başlık zaten
            çizginin ÜZERİNE bindirilir, bu blok gizlidir. */}
        <div className="relative mx-auto w-full max-w-7xl px-4 pt-2 sm:px-6 lg:hidden">
          <div className={rise} style={delay(40)}>
            <h2 className="font-bricolage text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl">
              {t('home.ctaTitle')}
              <span className="text-[var(--footer-accent)]">.</span>
            </h2>
          </div>
        </div>

        <div className={`relative pt-8 lg:pt-6 ${rise}`}>
          {/* Yalnız YATAYDA kırp (dar ekranda taşan sağ segment) — dikeyde
              görünür bırak ki neon ışıma üst/alttan KESİLMESİN. */}
          <div className="overflow-x-clip">
            <svg
              ref={lineSvgRef}
              viewBox="0 0 2071 322"
              fill="none"
              aria-hidden
              className="block h-auto w-full overflow-visible sm:min-w-[44rem]"
            >
              {/* NEON: altta blur'lu parlak kopya + üstte net çizgi; ikisi de
                  pathLength=1 dash ile KENDİNİ ÇİZEREK tamamlanır. */}
              {/* DİKKAT: non-scaling-stroke KULLANILMAZ — dash deseni ekran
                  pikseline dönüşüp pathLength=1 normalizasyonunu bozuyordu
                  (çizgi parçalı görünüyor, animasyon işlemiyordu). Kalınlıklar
                  viewBox birimindedir (2071 geniş → ~2 birim ≈ 1.5px ekran). */}
              <path
                d={LINE_PATH}
                pathLength={1}
                stroke={lineAccent}
                strokeWidth="9"
                style={{
                  strokeDasharray: 1,
                  strokeDashoffset: dashOffset,
                  transition: jawsMode
                    ? undefined
                    : 'stroke-dashoffset 1.8s ease-out',
                  filter: 'blur(6px)',
                  opacity: 0.55,
                }}
              />
              <path
                d={LINE_PATH}
                pathLength={1}
                stroke={lineAccent}
                strokeWidth="2"
                style={{
                  strokeDasharray: 1,
                  strokeDashoffset: dashOffset,
                  transition: jawsMode
                    ? undefined
                    : 'stroke-dashoffset 1.8s ease-out',
                  filter: `drop-shadow(0 0 6px ${lineAccent})`,
                }}
              />
            </svg>
          </div>
          {/* Başlık + CTA, üst (sağ, y≈0) ve alt (sol, y≈210) çizgi segmentleri
              arasına dikeyde ortalanır (orta nokta ≈ %33) ve SOLA yanaşır — her
              ekranda çizgiye bindirilir. Başlık solda, "Bize Ulaşın" yanında
              sağda; ortadaki "V" kancası ikisinin arasında boşta kalır. Alt
              yazı yalnızca lg+ (dar ekranda yalnızca buton yanda kalır). */}
          <div className="absolute inset-x-0 top-[33%] hidden -translate-y-1/2 lg:block">
            <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 sm:gap-8 sm:px-6">
              <h2 className="max-w-xl font-bricolage text-2xl font-semibold leading-tight tracking-tight text-white sm:text-4xl xl:text-5xl">
                <TypedTitle text={t('home.ctaTitle')} on={textsOn} />
                <span
                  className="text-[var(--footer-accent)]"
                  style={{
                    opacity: textsOn ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                    transitionDelay: textsOn
                      ? `${t('home.ctaTitle').length * 26}ms`
                      : '0ms',
                  }}
                >
                  .
                </span>
              </h2>
              {/* lg+: alt yazı + buton başlığın yanında sağda. Mobilde çizginin
                  altına iner (aşağıdaki blok). */}
              <div className="hidden flex-none items-center gap-5 lg:flex">
                <p className="max-w-xs text-sm text-[#CED1D1]/70">
                  {t('home.ctaSubtitle')}
                </p>
                <Link
                  to="/contact"
                  className="group inline-flex flex-none items-center gap-2 rounded-full bg-[var(--footer-accent)] px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_50px_-22px_var(--footer-accent)] transition-all hover:-translate-y-0.5 hover:bg-[var(--footer-accent-hover)]"
                >
                  {t('home.ctaButton')}
                  <ArrowRightOutlined className="text-xs transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Mobil: alt yazı + "Bize Ulaşın" — çizginin ALTINDA */}
        <div className="relative mx-auto w-full max-w-7xl px-4 pt-4 sm:px-6 lg:hidden">
          <div
            className={`flex items-center justify-between gap-4 ${rise}`}
            style={delay(60)}
          >
            <p className="max-w-xs text-sm text-[#CED1D1]/70">
              {t('home.ctaSubtitle')}
            </p>
            <Link
              to="/contact"
              className="group inline-flex flex-none items-center gap-2 rounded-full bg-[var(--footer-accent)] px-5 py-2.5 text-xs font-semibold text-white shadow-[0_18px_50px_-22px_var(--footer-accent)] transition-all hover:-translate-y-0.5 hover:bg-[var(--footer-accent-hover)] sm:px-6 sm:py-3 sm:text-sm"
            >
              {t('home.ctaButton')}
              <ArrowRightOutlined className="text-xs transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>

        {/* ── Orta: marka + link sütunları ── */}
        <div className="relative mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:py-14">
          {/* Mobilde link sütunları 2×2 yerleşir (tek kolonlu upuzun liste
              yerine); marka bloğu üstte tam genişlik. */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-7 sm:gap-10 lg:grid-cols-6">
            {/* Marka */}
            <div className={`col-span-2 ${rise}`} style={delay(80)}>
              <Logo />
              <p className="mt-4 max-w-xs text-sm text-[#CED1D1]/65">
                {t('footer.tagline')}
              </p>
              <a
                href={`mailto:${site.email}`}
                className="mt-5 inline-flex items-center gap-2 text-sm text-[#CED1D1]/75 transition-colors hover:text-[var(--footer-accent)]"
              >
                <MailOutlined />
                {site.email}
              </a>
              {/* Çerçevesiz sosyal ikonlar — kutu kaldırıldı, ikon eski kutu
                  boyutuna büyütüldü. */}
              <div className="mt-5 flex gap-5">
                {socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    className="flex h-9 w-9 items-center justify-center text-[26px] leading-none text-[#CED1D1]/70 transition-all hover:-translate-y-0.5 hover:text-[var(--footer-accent)]"
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Link sütunları — navbar menü sırasıyla */}
            <div className={rise} style={delay(160)}>
              <LinkColumn title={t('nav.programs')} links={programs} />
            </div>
            <div className={rise} style={delay(220)}>
              <LinkColumn
                title={
                  lang === 'en'
                    ? 'Ecosystem Development'
                    : 'Ekosistem Geliştirme'
                }
                links={ecosystem}
              />
            </div>
            <div className={rise} style={delay(280)}>
              <LinkColumn title={t('nav.discover')} links={discover} />
            </div>
            <div className={rise} style={delay(340)}>
              <LinkColumn title={t('nav.corporate')} links={corporate} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Alt bar — hafif ton farkıyla ayrılan bant ── */}
      <div className="relative border-t border-white/10 bg-white/[0.04]">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-5 text-xs text-[#CED1D1]/75 sm:flex-row sm:px-6">
          <span>
            © {year} {site.fullName}. {t('footer.rights')}
          </span>
          <span>{t('footer.location')}</span>
        </div>
      </div>
    </>
  );

  // Gömülü mod: EventsPortal'ın pinli sahnesini doldurur; opaklıkla (reveal)
  // Paydaşlar'ın üzerinde AYNI konumda crossfade ile belirir (kayma yok).
  if (jawsMode) {
    // SVG ŞEKLİNDE KAPANMA: kenarları LINE_PATH konturu olan İKİ panel —
    // üstteki yukarıdan iner, alttaki aşağıdan çıkar; reveal 0.6'da GERÇEK
    // çizginin render edildiği konumda (ölçülen jaw dönüşümü) BİRLEŞİRLER →
    // birleşme konturu, üzerinde neon çizginin aktığı çizgiyle birebir örtüşür.
    const closeT = cl(rev / 0.6);
    const off = (1 - closeT) * 1.5; // panellerin ekran dışından geliş mesafesi
    const jawT = (dy: number) =>
      `translate(${jaw.x0} ${jaw.y0 + dy}) scale(${jaw.sx} ${jaw.sy})`;
    // Kenar neonları: paneller birleşene kadar parlar; birleşince (reveal 0.6)
    // hızla sönerek yerini footer'ın TEK gerçek neon çizgisine bırakır.
    const edgeOpacity = cl(1 - (rev - 0.6) / 0.06);
    const jawsInner = (
      <>
        {/* Kapanma panellerinin clip tanımı — çizgi şekilli kenarlar.
            DİKKAT: clipPath içinde <g> GEÇERSİZ (yok sayılır → boş clip);
            transform doğrudan path'lere uygulanır. */}
        <svg width="0" height="0" aria-hidden className="absolute">
          <defs>
            <clipPath id="footer-jaws" clipPathUnits="objectBoundingBox">
              <path transform={jawT(-off)} d={JAW_TOP} />
              <path transform={jawT(off)} d={JAW_BOTTOM} />
            </clipPath>
          </defs>
        </svg>
        <div
          ref={rootRef}
          aria-hidden={rev < 0.5}
          className="absolute inset-0 z-20 flex flex-col overflow-hidden text-[#CED1D1]"
          style={{
            ...accentVars,
            // Ana sayfada NAVBAR CAMI: yarı saydam beyaz + arka plan blur.
            // Paneller kapandıkça koyu katman güçlenir → kapanış bittiğinde
            // arkadaki Paydaşlar artık seçilmez.
            background: homeSelf ? 'transparent' : FOOTER_BG,
            clipPath: 'url(#footer-jaws)',
            WebkitClipPath: 'url(#footer-jaws)',
          }}
        >
          {body}
        </div>
        {/* Panel KENARLARININ neon çizgileri — clip'in DIŞINDA (parlama
            kesilmesin), panellerle birlikte hareket eder; birleşme noktasında
            sönerek TEK gerçek neon çizgiye devrolur. */}
        {edgeOpacity > 0 && (
          <svg
            aria-hidden
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            className="pointer-events-none absolute inset-0 z-30 h-full w-full overflow-visible"
            style={{ opacity: edgeOpacity }}
          >
            {/* Frame altındaki neon çizgiyle AYNI düzen: geniş accent hale +
                açık iç parıltı + BEYAZ çekirdek */}
            {[-off, off].map((dy, i) => (
              <g key={i} transform={jawT(dy)}>
                <path
                  d={LINE_PATH}
                  fill="none"
                  stroke={lineAccent}
                  strokeWidth={14}
                  vectorEffect="non-scaling-stroke"
                  style={{ filter: 'blur(14px)', opacity: 0.6 }}
                />
                <path
                  d={LINE_PATH}
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth={4}
                  vectorEffect="non-scaling-stroke"
                  style={{ filter: 'blur(4px)', opacity: 0.45 }}
                />
                <path
                  d={LINE_PATH}
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                  style={{ filter: `drop-shadow(0 0 6px ${lineAccent})` }}
                />
              </g>
            ))}
          </svg>
        )}
      </>
    );
    // Gömülü: pinli sahneyi doğrudan doldurur. Ana sayfa: normal akışta,
    // tam ekran bir footer kabuğu içinde kendi scroll'uyla kapanır.
    if (embedded) return jawsInner;
    return (
      <footer
        ref={ref}
        // z-30: footer, Paydaşlar parkurunun (Home içeriği z-20) ÜZERİNDE
        // olmalı — aksi halde -mt ile bindirilen bu katmanın üstünde kalan
        // bölüm tıklamaları yutuyordu (CTA/link'ler çalışmıyordu).
        className="relative z-30 -mt-[220vh] h-[220vh] text-[#CED1D1]"
        style={{
          ...accentVars,
          background: 'transparent',
          // Paneller kapanmaya başlayana dek alttaki Paydaşlar etkileşimi
          // (logo linkleri/hover) engellenmesin.
          pointerEvents: rev > 0.05 ? 'auto' : 'none',
        }}
      >
        {/* Pinli sahne: paydaşlarla AYNI konumda; paneller yerinde kapanır. */}
        <div className="sticky top-0 flex h-[100lvh] flex-col overflow-hidden">
          {jawsInner}
        </div>
      </footer>
    );
  }

  // Standalone: masaüstünde tam ekran; MOBİLDE doğal yükseklik (dev boşluk yok).
  return (
    <footer
      ref={ref}
      className="relative flex flex-col overflow-hidden text-[#CED1D1] lg:min-h-[100lvh]"
      style={{
        ...accentVars,
        // Zemin HER SAYFADA şeffaf: footer, sayfanın kendi arka planını
        // (degrade + desen) kesintisiz sürdürür, üzerine ayrı bir renk
        // basılmaz. Daha önce burada koyu bir perde vardı; arka planı
        // örttüğü için kaldırıldı.
        background: 'transparent',
      }}
    >
      {body}
    </footer>
  );
};

export default Footer;
